import type { PaymentIntentResponse } from "@/api/online-ordering-service/model";

declare global {
  interface Window {
    Pixel?: new (cfg: unknown) => unknown;
    currentPaymobPixel?: unknown;
  }
}

export type PaymobLaunchOptions = {
  elementId?: string;
  paymentMethods?: string[];
  showSaveCard?: boolean;
  forceSaveCard?: boolean;
  onSuccess?: (response: unknown) => void;
  onCancel?: () => void;
  onFailure?: (err: unknown) => void;
};

// Pinned: `@latest` can change under us and defeats CDN caching.
const PAYMOB_PIXEL_BASE = "https://cdn.jsdelivr.net/npm/paymob-pixel@1.2.9";

let pixelPromise: Promise<NonNullable<Window["Pixel"]>> | null = null;

function addStylesheet(href: string): void {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Inject the Paymob Pixel SDK once and resolve when `window.Pixel` exists.
 * The SDK is ~1.9 MB from jsDelivr; it used to sit in index.html as a module
 * script ahead of the app, which blocked first render until it downloaded.
 */
export function loadPaymobPixel(): Promise<NonNullable<Window["Pixel"]>> {
  if (window.Pixel) return Promise.resolve(window.Pixel);
  if (pixelPromise) return pixelPromise;

  pixelPromise = new Promise<NonNullable<Window["Pixel"]>>((resolve, reject) => {
    addStylesheet(`${PAYMOB_PIXEL_BASE}/styles.css`);
    addStylesheet(`${PAYMOB_PIXEL_BASE}/main.css`);
    const script = document.createElement("script");
    script.type = "module";
    script.src = `${PAYMOB_PIXEL_BASE}/main.js`;
    script.onload = () => {
      // Module may assign window.Pixel on a later tick.
      const start = Date.now();
      const tick = () => {
        if (window.Pixel) resolve(window.Pixel);
        else if (Date.now() - start > 5000) reject(new Error("Paymob Pixel SDK not loaded"));
        else setTimeout(tick, 50);
      };
      tick();
    };
    script.onerror = () => reject(new Error("Paymob Pixel SDK failed to load"));
    document.head.appendChild(script);
  }).catch((err) => {
    pixelPromise = null; // allow a retry on the next checkout attempt
    throw err;
  });
  return pixelPromise;
}

/** Start downloading the SDK when the browser is idle, so checkout is warm. */
export function preloadPaymobPixel(): void {
  const start = () => void loadPaymobPixel().catch(() => {});
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(start, { timeout: 4000 });
  } else {
    setTimeout(start, 2000);
  }
}

export async function launchPaymobPixel(
  intent: PaymentIntentResponse,
  options: PaymobLaunchOptions = {},
): Promise<void> {
  const Pixel = await loadPaymobPixel();
  const elementId = options.elementId ?? "paymob-payment-container";

  const container = document.getElementById(elementId);
  if (!container) {
    throw new Error(`Paymob container #${elementId} not found`);
  }

  const config = {
    publicKey: intent.publicKey,
    clientSecret: intent.clientSecret,
    elementId,
    paymentMethods: options.paymentMethods ?? ["card", "google-pay", "apple-pay"],
    showSaveCard: options.showSaveCard ?? true,
    forceSaveCard: options.forceSaveCard ?? false,
    disablePay: false,
    afterPaymentComplete: (response: unknown) => {
      options.onSuccess?.(response);
    },
    onPaymentCancel: () => {
      options.onCancel?.();
    },
    customStyle: {
      Font_Family: "Inter, sans-serif",
      Font_Size_Label: "16",
      Font_Size_Input_Fields: "16",
      Font_Size_Payment_Button: "16",
      Color_Primary: "var(--primary-700)",
      Color_Border_Input_Fields: "#D1D5DB",
      Text_Color_For_Label: "#374151",
      Text_Color_For_Input_Fields: "#111827",
      Color_For_Text_Placeholder: "#9CA3AF",
      Width_of_Container: "100%",
      Radius_Border: "8",
    },
  };

  try {
    const instance = new Pixel(config);
    window.currentPaymobPixel = instance;
  } catch (err) {
    options.onFailure?.(err);
    throw err;
  }
}

export function cleanupPaymob(elementId = "paymob-payment-container"): void {
  try {
    const instance = window.currentPaymobPixel as
      | { destroy?: () => void; close?: () => void; remove?: () => void }
      | undefined;
    if (instance) {
      if (typeof instance.destroy === "function") instance.destroy();
      else if (typeof instance.close === "function") instance.close();
      else if (typeof instance.remove === "function") instance.remove();
    }
    delete window.currentPaymobPixel;
    const container = document.getElementById(elementId);
    if (container) container.innerHTML = "";
  } catch (err) {
    console.warn("[paymob] cleanup error", err);
  }
}
